import {
  IonButton,
  IonContent,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { useClinic } from '@/contexts';
import { useToast } from '@/hooks';
import {
  arrowBackOutline,
  checkmarkCircleOutline,
  warning
} from 'ionicons/icons';

import './UpdateLanguage.css';

interface UpdateLanguageProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const UpdateLanguage: React.FC<UpdateLanguageProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const { selectedClinic: clinic, updateClinicMutation } = useClinic();
  const displayToast = useToast();
  const [language, setLanguage] = useState<string>('en');

  useEffect(() => {
    if (clinic) {
      setLanguage(clinic.language || 'en');
    }
  }, [clinic?.language]);

  const copy = {
    header: (
      <>
        Update <Text color="primary">language</Text>
      </>
    ),
    message: 'Please select the language for your booking page.',
    buttonText: 'Update',
    selectLabel: 'Booking Page Language',
    selectPlaceholder: 'Select language'
  };

  const onNext = useCallback(async () => {
    if (clinic?.id) {
      await updateClinicMutation.mutateAsync({
        clinicId: clinic.id,
        clinic: {
          ...clinic,
          language: language
        }
      });
      displayToast({
        message: 'Booking page language updated successfully.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      onClose();
    }
  }, [language, clinic, updateClinicMutation, onClose]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="update-language">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} bottom={24}>
              <h1 className="update-language__header">{copy.header}</h1>
              <p className="update-language__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="update-language__form">
                <div className="update-language__input">
                  <IonSelect
                    label={copy.selectLabel}
                    labelPlacement="stacked"
                    placeholder={copy.selectPlaceholder}
                    value={language}
                    onIonChange={(e: CustomEvent) =>
                      setLanguage(e.detail.value)
                    }
                  >
                    <IonSelectOption value="en">English</IonSelectOption>
                    <IonSelectOption value="tl">Filipino</IonSelectOption>
                  </IonSelect>
                </div>
                <div className="update-language__clinic-warning">
                  <Icon icon={warning} />
                  <Text color="warning">
                    <strong>Caution:</strong> This setting will apply to the
                    entire booking page of your clinic, affecting all dentists.
                  </Text>
                </div>
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="next-button"
                onClick={onNext}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={updateClinicMutation.isPending}
              >
                <Icon slot="start" icon={checkmarkCircleOutline} />
                {copy.buttonText}
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading
          isOpen={updateClinicMutation.isPending}
          message="Loading..."
          spinner="crescent"
        />
      </IonContent>
    </IonModal>
  );
};

export default UpdateLanguage;
